import { getIncludedVatAmount } from "@grandvisionhq/analytics";
import { isDefined, getLocalizedValue, getStringAttrValue, getAttrValue, getJoinedLocalizedAttrValues, getLocalizedAttrValue, getLocalizedString, formatPrice } from "@grandvisionhq/utils";
import { DiscountType } from "../../../../../../types.js";
import { createAltTag } from "../../../../../../utils/utils/create-alt-tag.js";
import { getDiscount } from "../../../../../../utils/utils/get-discount.js";
import { getDiscountDescription } from "../../../../../../utils/utils/get-discount-description.js";
import { getProductTag } from "../../../../../../utils/utils/get-product-tag.js";
const isFullVariantModel = (product) => "id" in product;
const isLightProductModel = (product) => "objectID" in product;
const mapProductProjectionToListingProduct = (product, intl, priceSegment) => {
  const { getLabel, locale } = intl;
  const labels = {
    lens: getLabel(`products.unit.contactLens`),
    lenses: getLabel(`products.unit.contactLenses`),
    perBox: getLabel(`products.perBox`)
  };
  const masterVariant = mapVariantInfo(product.masterVariant, locale, labels, priceSegment);
  const variants = product.variants.map((variant) => mapVariantInfo(variant, locale, labels, priceSegment)).filter(isDefined);
  if (!masterVariant) {
    console.error(`ERROR: No masterVariant found for product.slug: ${product.slug.en}`);
    return void 0;
  }
  if (!product.productType.obj?.name || product.productType.obj?.name === "Additional-cart-costs") {
    console.error(`ERROR: No valid productType received for product.slug: ${product.slug.en}`);
    console.error(`ERROR: productType: ${product.productType.obj?.name}`);
    return void 0;
  }
  const productType = product.productType.obj.name;
  const includedVatAmount = getIncludedVatAmount(product?.taxCategory?.obj?.rates);
  const categoryName = product.categories?.[0]?.obj?.name;
  const category = categoryName ? getLocalizedValue(categoryName, "en") : void 0;
  const isOnStock = isLightProductModel(product) ? product.isOnStock : void 0;
  const productOffer = isLightProductModel(product) ? product?.offering : void 0;
  const offering = productOffer ? {
    includesLenses: productOffer?.includesLenses
  } : void 0;
  const ratingsSummary = !isLightProductModel(product) ? {
    average: product.ratingsSummary?.average,
    reviewCount: product.ratingsSummary?.reviewCount
  } : void 0;
  return {
    category,
    //used for structured data (en only)
    includedVatAmount,
    isOnStock,
    masterVariant,
    name: {
      en: product.name.en,
      //used for structured data
      localized: getLocalizedValue(product.name, locale)
    },
    objectID: isLightProductModel(product) ? product.objectID : "",
    offering,
    productType,
    ratingsSummary,
    //used for structured data
    slug: getLocalizedValue(product.slug, locale),
    variants,
    isOfferedWithPrescriptions: Boolean(product.masterVariant.isOfferedWithPrescriptions)
  };
};
const mapVariantInfo = (variant, locale, labels, priceSegment) => {
  const priceMap = !isFullVariantModel(variant) ? variant.priceMap : void 0;
  const price = mapPrice(variant.prices, locale, priceMap, priceSegment);
  const altTag = createAltTag({ variant, locale, labels });
  if (!price) {
    console.error(`ERROR: no valid price for variant with sku: ${variant.sku}`);
    return void 0;
  }
  const packageOption = getStringAttrValue(variant, "packageOption");
  const solutionType = getStringAttrValue(variant, "solutionType");
  const power = getStringAttrValue(variant, "power");
  const packageQuantity = getAttrValue(variant, "packageQuantity") ? Number(getAttrValue(variant, "packageQuantity")) : void 0;
  const priceIncluded = getAttrValue(variant, "priceIncluded");
  const priceIncludedAmount = priceIncluded && typeof priceIncluded === "object" ? Number(priceIncluded.centAmount) : void 0;
  const isExclusiveBrand = isDefined(getAttrValue(variant, "exclusiveBrand")) ? Boolean(getAttrValue(variant, "exclusiveBrand")) : void 0;
  const frameColor = {
    en: getJoinedLocalizedAttrValues(variant, "frameColor", "en"),
    localized: getJoinedLocalizedAttrValues(variant, "frameColor", locale)
  };
  const lensColor = getJoinedLocalizedAttrValues(variant, "lensColor", locale);
  const frameSize = getLocalizedAttrValue(variant, "frameSize", locale);
  const consolidationFacet = frameSize ? [frameColor.localized, lensColor].filter(Boolean).join("-") : null;
  return {
    brand: {
      name: getStringAttrValue(variant, "brand") || "",
      model: getStringAttrValue(variant, "brandModel") || "",
      modelCode: getStringAttrValue(variant, "brandModelCode") || ""
    },
    erpId: getStringAttrValue(variant, "erpId") || "",
    description: getLocalizedAttrValue(variant, "description", locale),
    frame: {
      color: frameColor,
      material: { en: getLocalizedAttrValue(variant, "frameMaterial", "en") },
      shape: { en: getLocalizedAttrValue(variant, "frameShape", "en") },
      type: { en: getLocalizedAttrValue(variant, "frameType", "en") }
    },
    faceShape: { en: getJoinedLocalizedAttrValues(variant, "faceShape", "en") },
    gender: { en: getJoinedLocalizedAttrValues(variant, "gender", "en") },
    lensColor: getJoinedLocalizedAttrValues(variant, "lensColor", locale),
    lensType: { en: getLocalizedAttrValue(variant, "lensType", "en") },
    replacementFrequency: getLocalizedAttrValue(variant, "replacementFrequency", locale),
    material: getLocalizedAttrValue(variant, "material", locale),
    accessoryType: {
      en: getLocalizedAttrValue(variant, "accessoryType", "en"),
      localized: getLocalizedAttrValue(variant, "accessoryType", locale)
    },
    accessoryColor: {
      en: getJoinedLocalizedAttrValues(variant, "accessoryColor", "en"),
      localized: getJoinedLocalizedAttrValues(variant, "accessoryColor", locale)
    },
    contactLensCareType: getLocalizedAttrValue(variant, "contactLensCareType", locale),
    isInsuranceReimbursable: Boolean(getAttrValue(variant, "isInsuranceReimbursable")),
    power,
    priceIncludedAmount,
    packageOption,
    packageQuantity,
    solutionType,
    isExclusiveBrand,
    name: variant.name || "",
    sku: String(variant.sku),
    tag: getProductTag(variant, locale),
    images: variant.media?.images.map((img) => ({ ...img, altTag })),
    pricing: {
      bestPrice: variant.bestPrice,
      price,
      pricePerUnit: variant.pricePerUnit || void 0
    },
    consolidationFacet
  };
};
const getPriceMapItemDiscount = (discount, currencyCode, locale) => {
  const discountValue = discount.value;
  if (typeof discountValue === "undefined" || discountValue === 0) {
    return void 0;
  }
  if (discount.type === DiscountType.Absolute) {
    return `-${formatPrice({
      locale,
      value: discountValue,
      currency: currencyCode,
      isCentAmount: false
    })}`;
  }
  if (discount.type === DiscountType.Relative) {
    return `-${discountValue}%`;
  }
  return void 0;
};
const mapPriceFromPriceMap = (priceItem, locale) => {
  const originalPrice = priceItem.originalPrice?.amount;
  const currencyCode = priceItem.originalPrice?.currencyCode;
  const discountPrice = priceItem.discountedPrice?.price?.amount;
  const discountCurrencyCode = priceItem.discountedPrice?.price?.currencyCode;
  const discountInformation = priceItem.discountedPrice?.discountInformation;
  if (!originalPrice || !currencyCode) {
    return void 0;
  }
  return {
    originalPrice: originalPrice * 100,
    isCentAmount: true,
    currencyCode,
    discount: discountPrice && discountCurrencyCode ? {
      price: discountPrice * 100,
      badge: discountInformation ? getPriceMapItemDiscount(discountInformation, discountCurrencyCode, locale) : void 0,
      description: discountInformation ? getLocalizedString(discountInformation.description, locale) : void 0,
      validFrom: priceItem.discountedPrice?.discountInformation?.validity?.validFrom,
      validUntil: priceItem.discountedPrice?.discountInformation?.validity?.validUntil
    } : void 0
  };
};
const mapPrice = (prices = [], locale, priceMap, priceSegment) => {
  if (priceMap && priceSegment && priceMap[priceSegment]) {
    return mapPriceFromPriceMap(priceMap[priceSegment], locale);
  }
  const price = prices[0];
  const discount = price?.discounted?.value.centAmount;
  if (!price) {
    return void 0;
  }
  return {
    originalPrice: price.value.centAmount,
    currencyCode: price.value.currencyCode,
    isCentAmount: true,
    discount: discount ? {
      description: getDiscountDescription(prices, locale),
      price: discount,
      badge: getDiscount(prices, locale),
      validFrom: price?.discounted?.discount?.obj?.validFrom,
      validUntil: price?.discounted?.discount?.obj?.validUntil
    } : void 0
  };
};
export {
  mapProductProjectionToListingProduct
};
//# sourceMappingURL=map-product-projection-to-listing-product.js.map
